import { useEffect, useState, useMemo, useCallback } from 'react';
import { listAuditLogs } from '../../api/audit';
import type { AuditLogEntry } from '../../api/audit';
import { ActivityFeed } from './ActivityFeed';
import { useTranslation } from 'react-i18next';
import styles from './ActivityPage.module.css';

const ACTION_KEYS: Record<string, string> = {
  create_project: 'createdProject',
  update_project: 'updatedProject',
  delete_project: 'deletedProject',
  create_user: 'createdUser',
  update_user: 'updatedUser',
  deactivate_user: 'deactivatedUser',
  create_org: 'createdOrganization',
  update_org: 'updatedOrganization',
  delete_org: 'deactivatedOrganization',
  save_snapshot: 'savedVersion',
  rollback_snapshot: 'rolledBackVersion',
  login: 'loggedIn',
};

const PAGE_SIZES = [10, 25, 50] as const;

export function ActivityPage() {
  const { t } = useTranslation(['activity', 'common']);
  const tAction = (action: string) => {
    const key = ACTION_KEYS[action];
    return key ? t(`activity:${key}`) : action;
  };
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [pageSize, setPageSize] = useState<number>(25);
  const [page, setPage] = useState(0);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listAuditLogs({
        limit: 200,
        offset: 0,
      });
      setEntries(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  // Client-side filtering
  const filtered = useMemo(() => {
    let result = entries;
    if (actionFilter) {
      result = result.filter((e) => e.action === actionFilter);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((e) => {
        const detail = e.details as Record<string, string> | null;
        const name = detail?.name ?? detail?.email ?? '';
        return (
          e.action.toLowerCase().includes(term) ||
          name.toLowerCase().includes(term) ||
          (e.resource_type ?? '').toLowerCase().includes(term)
        );
      });
    }
    if (dateFrom) {
      const from = new Date(dateFrom);
      result = result.filter((e) => new Date(e.created_at) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo + 'T23:59:59');
      result = result.filter((e) => new Date(e.created_at) <= to);
    }
    return result;
  }, [entries, actionFilter, searchTerm, dateFrom, dateTo]);

  // Stats
  const stats = useMemo(() => {
    const today = new Date().toDateString();
    const todayCount = entries.filter((e) => new Date(e.created_at).toDateString() === today).length;
    const actionCounts: Record<string, number> = {};
    for (const e of entries) {
      actionCounts[e.action] = (actionCounts[e.action] ?? 0) + 1;
    }
    let topAction = '';
    let topCount = 0;
    for (const [action, count] of Object.entries(actionCounts)) {
      if (count > topCount) { topAction = action; topCount = count; }
    }
    return { total: entries.length, todayCount, topAction, topCount };
  }, [entries]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginatedIds = useMemo(() => {
    const start = page * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [actionFilter, searchTerm, dateFrom, dateTo, pageSize]);

  return (
    <div className={styles.page}>
      <h2 className={styles.title}>{t('activity:title')}</h2>

      {/* Summary stats */}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{stats.total}</div>
          <div className={styles.statLabel}>{t('activity:totalEvents')}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{stats.todayCount}</div>
          <div className={styles.statLabel}>{t('activity:today')}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{tAction(stats.topAction) || '\u2014'}</div>
          <div className={styles.statLabel}>{t('activity:mostFrequent', { count: stats.topCount })}</div>
        </div>
      </div>

      {/* Filter bar */}
      <div className={styles.filterBar}>
        <select
          className={styles.filterSelect}
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
        >
          <option value="">{t('activity:allActions')}</option>
          {Object.entries(ACTION_KEYS).map(([key, i18nKey]) => (
            <option key={key} value={key}>{t(`activity:${i18nKey}`)}</option>
          ))}
        </select>

        <input
          type="date"
          className={styles.filterInput}
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          placeholder={t('common:from')}
        />
        <input
          type="date"
          className={styles.filterInput}
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          placeholder={t('common:to')}
        />

        <input
          type="text"
          className={styles.filterInput}
          placeholder={t('activity:searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: 1, minWidth: 120 }}
        />
      </div>

      {/* Feed */}
      <div className={styles.panel}>
        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('activity:loadingActivity')}</div>
        ) : paginatedIds.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('activity:noMatchingEvents')}</div>
        ) : (
          <ActivityFeed entries={paginatedIds} />
        )}
      </div>

      {/* Pagination */}
      <div className={styles.paginationBar}>
        <div className={styles.pageSizeSelect}>
          <span>{t('common:show')}</span>
          <select
            className={styles.filterSelect}
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <span>{t('common:perPage')}</span>
        </div>
        <div className={styles.pageControls}>
          <button
            className={styles.pageBtn}
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            {t('common:prev')}
          </button>
          <span className={styles.pageInfo}>
            {page + 1} / {totalPages} ({filtered.length} {t('common:results')})
          </span>
          <button
            className={styles.pageBtn}
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('common:next')}
          </button>
        </div>
      </div>
    </div>
  );
}
